import React, { useState, useEffect, useCallback } from 'react';
import { useColorModeValue } from '@chakra-ui/react';

import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, Select, FormControl, FormLabel, Input,
} from '@chakra-ui/react';
import Default from 'components/fields/InputField';

import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_OPTIONS } from 'views/admin/default/components/googleMapsLoader';

const timestampToDatetimeLocal = (ts) => {
  if (!ts) return '';
  const date = new Date(ts);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const datetimeLocalToTimestamp = (dtString) => {
  if (!dtString) return null;
  return new Date(dtString).getTime();
};



export default function EditDeviceDetailModal({ initialData = {}, isOpen, onClose, onSave }) {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_API_OPTIONS);
  const inputColor = useColorModeValue("gray.800", "white");

  // Use state to hold the input element for callback ref
  const [locationInputElem, setLocationInputElem] = useState(null);

  const backgroundColor = useColorModeValue('#fff', '#111316ff');
  const modalBg = useColorModeValue('#fff', 'navy.700');
  const textColor = useColorModeValue('#1a202c', '#f0f0f0');
  const highlightColor = useColorModeValue('#f0f0f0', '#37383aff');
  const hoverBgColor = useColorModeValue('#e9e9e9ff', '#2d2e30ff');
  const hoverColor = useColorModeValue('#110101ff', '#f0f0f0ff');

  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .pac-container {
        z-index: 2000 !important;
        border-radius: 10px;
        background-color: ${backgroundColor} !important;
        box-shadow: 0 5px 15px rgba(0,0,0,0.2);
      }

      .pac-item {
        padding: 10px;
        font-size: 14px;
        color: ${textColor} !important;
      }

      .pac-item:hover {
        background-color: ${hoverBgColor} !important;
        color: ${hoverColor} !important;
      }

      .pac-item-selected {
        background-color: ${highlightColor} !important;
      }

      .pac-item .pac-item-query {
        font-weight: 500;
        color: ${textColor} !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, [backgroundColor, textColor, highlightColor]);

  const [formData, setFormData] = useState({
    id: initialData?.details?.id,
    machineModel: initialData.machineModel || '',
    serialNumber: initialData.serialNumber || '',
    active: initialData.active ?? '',
    warranty: timestampToDatetimeLocal(initialData.warranty) || '',
    location: {
      lat: initialData?.location?.lat || 28.6139,
      lng: initialData?.location?.lng || 77.2090,
      loc: initialData?.location?.loc || '',
    },
  });

  // Initialize Google Autocomplete only when input ref is set, modal open and api loaded
  useEffect(() => {
    if (!isLoaded || !locationInputElem || !isOpen) return;

    const autocomplete = new window.google.maps.places.Autocomplete(locationInputElem);

    const onPlaceChanged = () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const loc = place.formatted_address || place.name || '';

      setFormData((prev) => ({
        ...prev,
        location: {
          lat,
          lng,
          loc,
        },
      }));
    };

    autocomplete.addListener('place_changed', onPlaceChanged);

    return () => {
      window.google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [isLoaded, locationInputElem, isOpen]);

  // Update form when initial data changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData?.details?.id,
        machineModel: initialData.machineModel || '',
        serialNumber: initialData.serialNumber || '',
        active: initialData.active ?? '',
        warranty: timestampToDatetimeLocal(initialData.warranty) || '',
        location: {
          lat: initialData?.location?.lat || 28.6139,
          lng: initialData?.location?.lng || 77.2090,
          loc: initialData?.location?.loc || '',
        },
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'location') {
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          loc: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveClick = () => {
    const dataToSave = {
      ...formData,
      warranty: datetimeLocalToTimestamp(formData.warranty),
    };
    onSave(dataToSave);
  };

  // Fields config
  const fields = [
    {
      name: 'machineModel',
      label: 'Machine Model',
      placeholder: 'Enter machine model',
      type: 'text',
      isDisabled: true,
    },
    {
      name: 'serialNumber',
      label: 'Serial Number',
      placeholder: 'Enter serial number',
      type: 'text',
      isDisabled: true,
    },
    {
      name: 'active',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
      isDisabled: true,
    },
    {
      name: 'location',
      label: 'Location',
      placeholder: 'Search location',
      type: 'location-autocomplete',
      isDisabled: false,
    },
    {
      name: 'warranty',
      label: 'Warranty',
      placeholder: 'Enter warranty date',
      type: 'datetime-local',
      isDisabled: false,
    },
  ];

  // Callback ref for input element
  const setLocationInputRef = useCallback((node) => {
    setLocationInputElem(node);
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="2rem" p="1rem" boxShadow="lg" bg={modalBg}>
        <ModalHeader>Edit Device</ModalHeader>
        <ModalCloseButton mt="1rem" />
        <ModalBody>
          {fields.map((field) => (
            <FormControl key={field.name} mb={4}>
              <FormLabel>{field.label}</FormLabel>
              {field.type === 'select' ? (
                <Select
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={`Select ${field.label}`}
                  disabled={field.isDisabled}
                  borderRadius="2rem"
                  _disabled={{
                    opacity: 1,
                    color: { inputColor },
                    cursor: 'not-allowed',
                    fontWeight: "500"
                  }}
                >
                  {field.options.map((option) => (
                    <option key={option.value.toString()} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : field.type === 'location-autocomplete' ? (
                <Input
                  name="location"
                  ref={setLocationInputRef}
                  placeholder={field.placeholder}
                  value={formData.location.loc}
                  onChange={handleChange}
                  isDisabled={field.isDisabled}
                  borderRadius="2rem"
                  autoComplete="off"
                  color={inputColor}
                />
              ) : (
                <Default
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name]}
                  onChange={handleChange}
                  disabled={field.isDisabled}
                  _disabled={{
                    opacity: 1,
                    color: { inputColor },
                    cursor: 'not-allowed',
                    fontWeight: "500"
                  }}
                />
              )}
            </FormControl>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="green"
            variant="ghost"
            mr={3}
            borderWidth="2px"
            borderColor="green.200"
            onClick={handleSaveClick}
          >
            Save
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            borderWidth="2px"
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
